/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addLine,
  updateLineParams,
  repriceLine,
  removeLine,
  clearLines,
  setInvoice,
  resetCart,
  setEntryRoute,
  clearEntryRoute,
} from "../store/slices/cartSlice";
import { CART_LINE_TYPES } from "../constants/constants";
import { cartRegistry } from "../utils/cartRegistry";

export function useCart() {
  const dispatch = useDispatch();
  const { lines, invoice, entryRoute } = useSelector((state) => state.cart);

  const actions = useMemo(
    () => ({
      addLine: (payload) => dispatch(addLine(payload)),
      updateLineParams: (payload) => dispatch(updateLineParams(payload)),
      repriceLine: (payload) => dispatch(repriceLine(payload)),
      removeLine: (payload) => dispatch(removeLine(payload)),
      clearLines: () => dispatch(clearLines()),
      setInvoice: (payload) => dispatch(setInvoice(payload)),
      resetCart: () => dispatch(resetCart()),
      setEntryRoute: (payload) => dispatch(setEntryRoute(payload)),
      clearEntryRoute: () => dispatch(clearEntryRoute()),
    }),
    [dispatch]
  );

  const productLines = useMemo(
    () => lines.filter((l) => l.type === CART_LINE_TYPES.PRODUCT),
    [lines]
  );

  const findLine = (type, predicate) =>
    lines.find((l) => l.type === type && predicate(l.params, l));

  const canEditQty = (line) => !!cartRegistry[line.type]?.canEditQty;

  const setLineQty = useCallback(
    (line, qty) => {
      if (qty <= 0) {
        dispatch(removeLine({ clientId: line.clientId }));
        return;
      }
      dispatch(updateLineParams({ clientId: line.clientId, patch: { qty } }));
    },
    [dispatch]
  );

  const deleteLine = useCallback(
    (line) => dispatch(removeLine({ clientId: line.clientId })),
    [dispatch]
  );

  const totalQuantity = useMemo(
    () => lines.reduce((sum, l) => sum + (l.params.qty ?? 1), 0),
    [lines]
  );

  return {
    lines,
    productLines,
    invoice,
    entryRoute,
    totalQuantity,
    findLine,
    canEditQty,
    setLineQty,
    deleteLine,
    ...actions,
  };
}
